import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    TextField,
    Typography,
    Paper,
    Snackbar,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Switch,
} from "@mui/material";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { createProduct, updateProduct } from "../../Apis/ProductAPI";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const ProductForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const productid = location.state?.id || "1"; // categoryId
    const passedProduct = location.state?.product || null;

    const [formData, setFormData] = useState({
        categoryId: productid,
        name: "",
        description: "",
        quantity: "",
        quantity_type: "kg",
        pieces: "",
        serves: "",
        about_product: "",
        est_delivery_time: "",
        price: "",
        offer_percentage: "",
        originalPrice: "",
        status: true,
    });

    const [imageFile, setImageFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");

    useEffect(() => {
        if (id && passedProduct) {
            setFormData({
                categoryId: productid || "1",
                name: passedProduct.name || "",
                description: passedProduct.description || "",
                quantity: passedProduct.quantity || "",
                quantity_type: passedProduct.quantity_type || "kg",
                pieces: passedProduct.pieces || "",
                serves: passedProduct.serves || "",
                about_product: passedProduct.about_product || "",
                est_delivery_time: passedProduct.est_delivery_time || "",
                price: passedProduct.price || "",
                offer_percentage: passedProduct.offer_percentage || "",
                originalPrice: passedProduct.originalPrice || "",
                status: Boolean(passedProduct.status),
            });
            setPreview(passedProduct.imageUrl || null);
        }
    }, [id, passedProduct, productid]);

    // Handle text/number inputs
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    // Handle rich text (Quill)
    const handleQuillChange = (value) => {
        setFormData((prev) => ({
            ...prev,
            about_product: value,
        }));
    };

    // Handle switch
    const handleStatusChange = (e) => {
        setFormData((prev) => ({ ...prev, status: e.target.checked }));
    };

    // Handle image upload
    const handleImageChange = (e) => {
        const file = e.target.files[0];
        setImageFile(file);
        if (file) setPreview(URL.createObjectURL(file));
    };

    // Submit
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.name || !formData.price || !formData.about_product || (!id && !imageFile)) {
            setErrorMsg("Please fill all required fields and upload an image.");
            return;
        }

        const data = new FormData();
        Object.entries(formData).forEach(([key, value]) => {
            data.append(key, value);
        });

        if (imageFile) {
            data.append("productImage", imageFile);
        }

        try {
            setLoading(true);
            if (id) {
                await updateProduct(id, data);
            } else {
                await createProduct(data);
            }
            setSuccess(true);
            setTimeout(() => navigate(-1), 1200);
        } catch (err) {
            console.error(err);
            setErrorMsg(id ? "Failed to update product." : "Failed to create product.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            <Paper sx={{ width: "50%", p: 4 }}>
                <Typography variant="h5" gutterBottom>
                    {id ? "Update Product" : "Create Product"}
                </Typography>
                <form onSubmit={handleSubmit}>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                        <TextField
                            label="Name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            size="small"
                            sx={{ flex: "1 1 48%" }}
                        />
                         <br/>
                        <TextField
                            label="Description"
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            size="large"
                            sx={{ flex: "1 1 48%" }}
                        />
                        <br/>
                        <TextField
                            label="Quantity"
                            name="quantity"
                            value={formData.quantity}
                            onChange={handleChange}
                            size="small"
                            sx={{ flex: "1 1 48%" }}
                        />
                        <FormControl size="small" sx={{ flex: "1 1 48%" }}>
                            <InputLabel>Quantity Type</InputLabel>
                            <Select
                                name="quantity_type"
                                value={formData.quantity_type}
                                label="Quantity Type"
                                onChange={handleChange}
                            >
                                <MenuItem value="kg">kg</MenuItem>
                                <MenuItem value="g">g</MenuItem>
                                <MenuItem value="piece">piece</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            label="Pieces"
                            name="pieces"
                            value={formData.pieces}
                            onChange={handleChange}
                            size="small"
                            sx={{ flex: "1 1 48%" }}
                        />
                        <TextField
                            label="Serves"
                            name="serves"
                            value={formData.serves}
                            onChange={handleChange}
                            size="small"
                            sx={{ flex: "1 1 48%" }}
                        />
                        <TextField
                            label="Price"
                            name="price"
                            value={formData.price}
                            onChange={handleChange}
                            size="small"
                            sx={{ flex: "1 1 48%" }}
                        />
                        <TextField
                            label="Offer Percentage"
                            name="offer_percentage"
                            value={formData.offer_percentage}
                            onChange={handleChange}
                            size="small"
                            sx={{ flex: "1 1 48%" }}
                        />
                        <TextField
                            label="Est Delivery Time"
                            name="est_delivery_time"
                            value={formData.est_delivery_time}
                            onChange={handleChange}
                            size="small"
                            sx={{ flex: "1 1 48%" }}
                        />
                        <TextField
                            label="Original Price"
                            name="originalPrice"
                            value={formData.originalPrice}
                            onChange={handleChange}
                            size="small"
                            sx={{ flex: "1 1 48%" }}
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.status}
                                    onChange={handleStatusChange}
                                />
                            }
                            label="Active"
                        />
                        {/* Quill Editor for About Product */}
                        <Box sx={{ flex: "1 1 100%", mt: 2 }}>
                            <Typography variant="body1" sx={{ mb: 1 }}>
                                About Product
                            </Typography>
                            <ReactQuill
                                theme="snow"
                                value={formData.about_product}
                                onChange={handleQuillChange}
                                style={{ height: 200, marginBottom: 40 }}
                            />
                        </Box>
                    </Box>

                    <Button variant="contained" component="label" sx={{ mt: 2 }}>
                        Upload Image
                        <input type="file" hidden onChange={handleImageChange} />
                    </Button>
                    {preview && (
                        <Box sx={{ mt: 2, textAlign: "center" }}>
                            <Typography variant="body2">Image Preview:</Typography>
                            <img
                                src={preview}
                                alt="preview"
                                style={{ width: 150, marginTop: 8, borderRadius: 8 }}
                            />
                        </Box>
                    )}

                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        fullWidth
                        sx={{ mt: 3 }}
                        disabled={loading}
                    >
                        {loading ? (id ? "Updating..." : "Creating...") : id ? "Update Product" : "Create Product"}
                    </Button>
                </form>

                <Snackbar
                    open={success}
                    autoHideDuration={3000}
                    onClose={() => setSuccess(false)}
                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                >
                    <Alert severity="success" variant="filled" onClose={() => setSuccess(false)}>
                        {id ? "Product updated successfully!" : "Product created successfully!"}
                    </Alert>
                </Snackbar>

                <Snackbar
                    open={!!errorMsg}
                    autoHideDuration={4000}
                    onClose={() => setErrorMsg("")}
                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                >
                    <Alert severity="error" variant="filled" onClose={() => setErrorMsg("")}>
                        {errorMsg}
                    </Alert>
                </Snackbar>
            </Paper>
        </Box>
    );
};

export default ProductForm;
